/**
 * EASING_CATALOG: the 29 RPE easings (F§6.5, E§1.3, P§1.5, D§4.12) with family, variant, Phira tween id, RPE
 * name, the plain strength word (rows of the Guided easing grid) and shape word (columns: In = "Slow start",
 * Out = "Slow end", InOut = "Slow both"), overshoot flag (Back and Elastic leave [0, 1]), the two curves Phira
 * draws differently from RPE (23 InOutBack, 29 InOutElastic), and search aliases.
 *
 * `searchEasings` is the picker's fuzzy search: RPE numbers ("4", "23"), compact names ("outquad", "outQuad",
 * "ease-out-quad"), spaced words in any order ("out quad", "quad out"), initialisms ("oq", "ioq", "oqn"),
 * plain words ("soft slow end", "bouncy") and prefixes ("out qu" -> OutQuad, OutQuart, OutQuint), best first.
 */

import { RPE_TWEEN_MAP } from './rpeMap';
import type { EaseFamily, EaseShapeWord, EaseStrength, EaseVariant, EasingCatalogEntry } from './types';

type CurveFamily = Exclude<EaseFamily, 'linear'>;

const FAMILY_NAME: Readonly<Record<CurveFamily, string>> = {
  sine: 'Sine',
  quad: 'Quad',
  cubic: 'Cubic',
  quart: 'Quart',
  quint: 'Quint',
  expo: 'Expo',
  circ: 'Circ',
  back: 'Back',
  elastic: 'Elastic',
  bounce: 'Bounce',
};

const STRENGTH: Readonly<Record<CurveFamily, EaseStrength>> = {
  sine: 'gentle',
  quad: 'soft',
  cubic: 'medium',
  quart: 'strong',
  quint: 'stronger',
  expo: 'extreme',
  circ: 'sharp',
  back: 'overshoot',
  elastic: 'springy',
  bounce: 'bouncy',
};

/** Short family codes for initialisms (first code = the conventional one). */
const FAMILY_CODES: Readonly<Record<CurveFamily, readonly string[]>> = {
  sine: ['s', 'si'],
  quad: ['q', 'qd'],
  cubic: ['c', 'cu'],
  quart: ['qr', 'q4'],
  quint: ['qn', 'q5'],
  expo: ['e', 'ex'],
  circ: ['ci', 'cr'],
  back: ['b', 'bk'],
  elastic: ['el', 'l'],
  bounce: ['bo', 'bn'],
};

const VARIANT_NAME: Readonly<Record<EaseVariant, string>> = { in: 'In', out: 'Out', inout: 'InOut' };
const VARIANT_CODES: Readonly<Record<EaseVariant, readonly string[]>> = { in: ['i'], out: ['o'], inout: ['io'] };
const SHAPE: Readonly<Record<EaseVariant, EaseShapeWord>> = { in: 'slowStart', out: 'slowEnd', inout: 'slowBoth' };

/** RPE easingType -> (family, variant) for 2..29 (F§6.5 table). */
const RPE_CURVES: readonly (readonly [easingType: number, family: CurveFamily, variant: EaseVariant])[] = [
  [2, 'sine', 'out'],
  [3, 'sine', 'in'],
  [4, 'quad', 'out'],
  [5, 'quad', 'in'],
  [6, 'sine', 'inout'],
  [7, 'quad', 'inout'],
  [8, 'cubic', 'out'],
  [9, 'cubic', 'in'],
  [10, 'quart', 'out'],
  [11, 'quart', 'in'],
  [12, 'cubic', 'inout'],
  [13, 'quart', 'inout'],
  [14, 'quint', 'out'],
  [15, 'quint', 'in'],
  [16, 'expo', 'out'],
  [17, 'expo', 'in'],
  [18, 'circ', 'out'],
  [19, 'circ', 'in'],
  [20, 'back', 'out'],
  [21, 'back', 'in'],
  [22, 'circ', 'inout'],
  [23, 'back', 'inout'],
  [24, 'elastic', 'out'],
  [25, 'elastic', 'in'],
  [26, 'bounce', 'out'],
  [27, 'bounce', 'in'],
  [28, 'bounce', 'inout'],
  [29, 'elastic', 'inout'],
];

function curveAliases(easingType: number, family: CurveFamily, variant: EaseVariant): string[] {
  const spacedVariant = variant === 'inout' ? 'in out' : variant;
  const aliases = new Set<string>([
    `${variant}${family}`,
    `${family}${variant}`,
    `ease${variant}${family}`,
    `${spacedVariant} ${family}`,
    `${family} ${spacedVariant}`,
  ]);
  for (const vc of VARIANT_CODES[variant]) for (const fc of FAMILY_CODES[family]) aliases.add(`${vc}${fc}`);
  aliases.add(String(easingType));
  return [...aliases];
}

function buildCatalog(): readonly EasingCatalogEntry[] {
  const entries: EasingCatalogEntry[] = [
    {
      easingType: 1,
      family: 'linear',
      variant: null,
      tweenId: RPE_TWEEN_MAP[1] ?? 2,
      rpeName: 'Linear',
      strength: null,
      shape: null,
      overshoots: false,
      differsInRpe: false,
      aliases: ['linear', 'lin', 'l', 'straight', 'constant speed', '1'],
    },
  ];
  for (const [easingType, family, variant] of RPE_CURVES) {
    entries.push({
      easingType,
      family,
      variant,
      tweenId: RPE_TWEEN_MAP[easingType] ?? 2,
      rpeName: `${VARIANT_NAME[variant]}${FAMILY_NAME[family]}`,
      strength: STRENGTH[family],
      shape: SHAPE[variant],
      overshoots: family === 'back' || family === 'elastic',
      differsInRpe: easingType === 23 || easingType === 29,
      aliases: curveAliases(easingType, family, variant),
    });
  }
  entries.sort((a, b) => a.easingType - b.easingType);
  return entries;
}

export const EASING_CATALOG: readonly EasingCatalogEntry[] = buildCatalog();

/** Lookup by RPE easingType 1..29; null for anything else. */
export function catalogEntry(easingType: number): EasingCatalogEntry | null {
  if (!Number.isInteger(easingType)) return null;
  return EASING_CATALOG[easingType - 1] ?? null;
}

/** The entry with the same family and the given variant (null when RPE has no number for it). */
export function catalogVariant(family: EaseFamily, variant: EaseVariant | null): EasingCatalogEntry | null {
  return EASING_CATALOG.find((e) => e.family === family && e.variant === variant) ?? null;
}

// ---------------------------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------------------------

const SHAPE_WORDS: Readonly<Record<EaseVariant, readonly string[]>> = {
  in: ['in', 'slow', 'start', 'accelerate'],
  out: ['out', 'slow', 'end', 'decelerate'],
  inout: ['inout', 'in', 'out', 'slow', 'both', 'io'],
};

interface SearchDoc {
  readonly entry: EasingCatalogEntry;
  readonly compactAliases: readonly string[];
  readonly compactNames: readonly string[];
  readonly variantWords: readonly string[];
  readonly familyWords: readonly string[];
}

const SEARCH_DOCS: readonly SearchDoc[] = EASING_CATALOG.map((entry) => {
  const compactAliases = entry.aliases.map(compact);
  const familyWords =
    entry.family === 'linear'
      ? ['linear', 'lin', 'straight', 'constant', 'speed', 'none']
      : [entry.family, entry.strength ?? ''];
  const variantWords = entry.variant === null ? [] : SHAPE_WORDS[entry.variant];
  const compactNames = [compact(entry.rpeName)];
  if (entry.variant !== null && entry.family !== 'linear') compactNames.push(`${entry.family}${entry.variant}`);
  return { entry, compactAliases, compactNames, variantWords, familyWords: familyWords.filter((w) => w !== '') };
});

function compact(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Lower-case word tokens: splits camelCase, separators and letter/digit boundaries; joins "in out" to "inout". */
function tokenize(query: string): string[] {
  const spaced = query
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  if (spaced === '') return [];
  const raw = spaced.split(' ').filter((t) => t !== 'ease' && t !== 'easing');
  const out: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    const t = raw[i] ?? '';
    if (t === 'in' && raw[i + 1] === 'out') {
      out.push('inout');
      i++;
    } else out.push(t);
  }
  return out;
}

function isSubsequence(needle: string, hay: string): boolean {
  let j = 0;
  for (let i = 0; i < hay.length && j < needle.length; i++) if (hay[i] === needle[j]) j++;
  return j === needle.length;
}

function tokenScore(doc: SearchDoc, tokens: readonly string[]): number {
  let score = 0;
  let variantHit = false;
  let familyHit = false;
  for (const t of tokens) {
    let best = 0;
    let slot: 'variant' | 'family' | null = null;
    for (const w of doc.variantWords) {
      const s =
        w === t ? (doc.entry.variant === 'inout' && (t === 'in' || t === 'out') ? 8 : 20) : w.startsWith(t) ? 10 : 0;
      if (s > best) {
        best = s;
        slot = 'variant';
      }
    }
    for (const w of doc.familyWords) {
      const s = w === t ? 20 : w.startsWith(t) ? 10 : 0;
      if (s > best) {
        best = s;
        slot = 'family';
      }
    }
    if (best === 0) return 0;
    score += best;
    if (slot === 'variant') variantHit = true;
    if (slot === 'family') familyHit = true;
  }
  const missing = (doc.variantWords.length > 0 && !variantHit ? 1 : 0) + (familyHit ? 0 : 1);
  return 600 + score - 15 * missing;
}

function scoreDoc(doc: SearchDoc, q: string, tokens: readonly string[]): number {
  let best = 0;
  if (/^\d+$/.test(q)) {
    const id = String(doc.entry.easingType);
    if (id === q) return 1000;
    return id.startsWith(q) ? 500 - id.length : 0;
  }
  if (doc.compactNames.includes(q)) best = 950;
  if (doc.compactAliases.includes(q)) best = Math.max(best, 900);
  if (tokens.length > 0) best = Math.max(best, tokenScore(doc, tokens));
  for (const name of doc.compactNames) {
    if (name.startsWith(q)) best = Math.max(best, 550 - (name.length - q.length));
    else if (name.includes(q)) best = Math.max(best, 400 - (name.length - q.length));
    else if (q.length >= 2 && isSubsequence(q, name)) best = Math.max(best, 200 - (name.length - q.length));
  }
  for (const alias of doc.compactAliases) {
    if (q.length >= 2 && alias.startsWith(q)) best = Math.max(best, 450 - (alias.length - q.length));
  }
  return best;
}

/** Fuzzy search over RPE names, aliases, plain words and numbers; best first (ties by RPE number). */
export function searchEasings(query: string): readonly EasingCatalogEntry[] {
  const q = compact(query);
  if (q === '') return [];
  const tokens = tokenize(query);
  const scored: { readonly entry: EasingCatalogEntry; readonly score: number }[] = [];
  for (const doc of SEARCH_DOCS) {
    const score = scoreDoc(doc, q, tokens);
    if (score > 0) scored.push({ entry: doc.entry, score });
  }
  scored.sort((a, b) => b.score - a.score || a.entry.easingType - b.entry.easingType);
  return scored.map((s) => s.entry);
}
